import React from 'react';
import styled from 'styled-components';

import { Product } from 'types/graphql-overrides';
import usePaths from 'hooks/use-paths';
import useUI from 'shared/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import EmptyLoadingState from 'src/components/empty-loading-state';
import { CarouselV2 } from 'src/components/carousel-v2/carousel-v2';
import { useQuickAddToCart } from 'src/components/product-carousel/use-quick-add-to-cart';
import { tracker } from 'src/utils/analytics';

import { BuyItAgainPromoCard } from './buy-it-again-promo-card';
import { BuyItAgainSimpleTile } from './buy-it-again-simple-tile';
import { BuyItAgainSubstituteModalController, useBuyItAgainSubstituteModal } from './buy-it-again-substitute-modal';
import { useBuyItAgainCarousel, BuyItAgainOutOfStockItem } from './use-buy-it-again-carousel';

export function BuyItAgainCarousel(): JSX.Element | null {
  const { dispensary } = useDispensary();
  const UI = useUI();
  const { href } = usePaths({ dispensary });

  const hasUserPaths = Boolean(UI.isEmbedded) || Boolean(UI.isStoreFront);
  const ordersPath = hasUserPaths ? `${href}/user/orders` : '/account/orders';

  const { items, loading, handleViewAllClick, viewportVisibility, trackerSource } = useBuyItAgainCarousel();
  const { handleAddToCart } = useQuickAddToCart();
  const { open: openSubstituteModal } = useBuyItAgainSubstituteModal();

  if (loading) {
    return <EmptyLoadingState isLoading={loading} showBorder={false} page='productCarousel' />;
  }

  if (!items.length) {
    return null;
  }

  const handleProductClick = (product: Product, index: number): void => {
    if (!trackerSource) {
      return;
    }
    tracker.setContext({ activeProductPosition: index });
    tracker.productClicked({ product, position: index, trackerSource });
    UI.activeProductSource = trackerSource;
  };

  const handleQuickAddClick = (product: Product, index: number): void => {
    if (!trackerSource) {
      return;
    }
    tracker.setContext({ activeProductPosition: index });
    tracker.productClicked({ product, position: index, trackerSource });
    UI.activeProductSource = trackerSource;
    void handleAddToCart(product, trackerSource);
  };

  const handleOutOfStockClick = (item: BuyItAgainOutOfStockItem): void => {
    void openSubstituteModal({
      originalProduct: item.originalProduct,
      substituteCandidates: item.substituteCandidates,
      trackerSource,
    });
  };

  return (
    <>
      <BuyItAgainSubstituteModalController />
      <CarouselV2
        ref={viewportVisibility.ref}
        title='Buy it again'
        enableLazyRendering
        lazyPlaceholder={<LazyPlaceholder />}
      >
        <Wrapper role='group' aria-roledescription='slide' aria-label='Buy it again order history'>
          <BuyItAgainPromoCard
            href={ordersPath}
            onClick={() => handleViewAllClick(ordersPath)}
            logoImage={dispensary?.logoImage}
          />
        </Wrapper>

        {items.map((item, index) => (
          <Wrapper
            key={item.type === 'available' ? item.product.id : item.originalProductId}
            role='group'
            aria-roledescription='slide'
            aria-label={item.type === 'available' ? item.product.name : item.originalProduct.Name ?? ''}
          >
            <BuyItAgainSimpleTile
              item={item}
              index={index}
              onProductClick={handleProductClick}
              onQuickAddClick={handleQuickAddClick}
              onOutOfStockClick={handleOutOfStockClick}
            />
          </Wrapper>
        ))}
      </CarouselV2>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
`;

// Overrides CarouselV2's default 400px-tall placeholder — this carousel's cards are
// much shorter, and the taller default was stretching the whole row's height and
// leaving a large empty gap below it while items were waiting to lazy-render in.
const LazyPlaceholder = styled.div`
  background-color: ${({ theme }) => theme.colors.grey[95]};
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  flex-shrink: 0;
  height: 152px;
  width: 300px;

  @media screen and (min-width: 1110px) {
    height: 198px;
    width: 380px;
  }
`;
